import { CURRENT_LEGAL_DOCUMENTS } from "../legal/legalDocuments";

/**
 * The schedule every trial lifecycle row is held to. Bump it whenever a
 * deadline below changes; rows stamped with an older version are reconciled
 * forward by `reconcileTrialLifecycleWithCurrentPolicy`, never backward.
 *
 * Offsets from enrollment; the legal copy counts days from one, so +4d is
 * "day 5".
 * 2026-08-12: access +3d, notices +4d and +6d (days 5 and 7), deletion +7d.
 * 2026-10-05: access +7d, notices +8d and +10d (days 9 and 11), deletion +11d.
 * 2026-10-13: access +7d, notices +8d and +10d. No deletion: an ended trial
 *   that does not convert is kept. Since 2026-10-20 (Free plan removed) it is
 *   paused until it picks a plan; the schedule did not change.
 */
export const TRIAL_POLICY_VERSION = "2026-10-13";

const DAY_MS = 24 * 60 * 60 * 1000;

/** Days of full access a standalone trial gets; copy derives from this. */
export const TRIAL_ACCESS_DAYS = 7;
export const TRIAL_ACCESS_DURATION_MS = TRIAL_ACCESS_DAYS * DAY_MS;
/**
 * The first post-trial notice, one day after access ends. The stored field
 * (`dayFiveEmailAt`) and scheduler action (`day_five_email`) keep their
 * original names; they mean "first post-trial notice", not calendar day five.
 */
export const TRIAL_FIRST_NOTICE_OFFSET_MS = TRIAL_ACCESS_DURATION_MS + DAY_MS;
/**
 * The final notice (`daySevenEmailAt` / `day_seven_email`, which likewise
 * mean "final post-trial notice"). It is the last scheduled lifecycle action.
 */
export const TRIAL_FINAL_NOTICE_OFFSET_MS = TRIAL_ACCESS_DURATION_MS + 3 * DAY_MS;
/**
 * Legacy: the date older policies deleted an unconverted trial. Rows still
 * store it (`deletionAt` is required), but nothing acts on it any more: an
 * ended trial is paused, not deleted.
 */
export const TRIAL_DELETION_OFFSET_MS = TRIAL_ACCESS_DURATION_MS + 4 * DAY_MS;
/** A trial imports at most this many email threads across all providers. */
export const TRIAL_EMAIL_THREAD_LIMIT = 500;
/** Silos before this template version cap the trial preview at 3 threads. */
export const TRIAL_EMAIL_PREVIEW_SILO_VERSION = 153;
export const LEGACY_TRIAL_EMAIL_THREAD_LIMIT = 3;
/**
 * Re-run version for finalized trial previews that selected no threads. Rows
 * stamped with a lower version (or none) get forced provider preview
 * refreshes until threads land or the attempts run out; raise it only when a
 * newer adapter fix needs another pass.
 *
 * v2: v1 marked rows done as soon as the refresh was scheduled, so a refresh
 * run that stalled, crashed or expired left the row at zero threads for good.
 * v2 re-opens those rows under attempt-based retries.
 */
export const TRIAL_EMAIL_ZERO_PREVIEW_REPAIR_VERSION = 2;
/** Forced refreshes per repair version before a still-empty row is retired. */
export const TRIAL_EMAIL_ZERO_PREVIEW_REPAIR_MAX_ATTEMPTS = 3;
/**
 * Wait after a scheduled refresh before the next attempt. A refresh that
 * worked has admitted threads by then and left the candidate range.
 */
export const TRIAL_EMAIL_ZERO_PREVIEW_REPAIR_RETRY_MS = 6 * 60 * 60 * 1000;
/**
 * A finalized preview is only treated as empty after this long. Finalization
 * can land before the admission that counts its first thread.
 */
export const TRIAL_EMAIL_ZERO_PREVIEW_REPAIR_SETTLE_MS = 60 * 60 * 1000;

/**
 * The trial thread limit a user's silo can hold. An unknown version is treated
 * as a pre-v153 silo, so a trial never asks an old silo for more than it takes.
 */
export function trialEmailThreadLimitForSiloVersion(templateVersion: unknown): number {
  return typeof templateVersion === "number" && templateVersion >= TRIAL_EMAIL_PREVIEW_SILO_VERSION
    ? TRIAL_EMAIL_THREAD_LIMIT
    : LEGACY_TRIAL_EMAIL_THREAD_LIMIT;
}
/** ...and only threads whose newest message is at most this old. */
export const TRIAL_EMAIL_WINDOW_MS = 90 * 24 * 60 * 60 * 1000;
export const TRIAL_EMAIL_SELECTION_POLICY =
  "bounded_counter_first_seen" as const;
/**
 * The cohort marker stamped on accounts created into the local-trial model.
 * It names the model, not the schedule: pending enrollments and admission
 * checks compare against it, so it stays fixed when the schedule changes.
 */
export const TRIAL_ENROLLMENT_COHORT_VERSION = "2026-08-12";

type TrialPayments = {
  subscriptionStatus?: unknown;
  subscriptionId?: unknown;
  customerId?: unknown;
  stripeCustomerId?: unknown;
  plan?: unknown;
};

export function isVerifiedPaidTrialConversion(payments: unknown): boolean {
  if (!payments || typeof payments !== "object" || Array.isArray(payments)) {
    return false;
  }
  const value = payments as TrialPayments;
  if (value.plan === "lifetime") return true;
  const status = String(value.subscriptionStatus || "").trim().toLowerCase();
  const subscriptionId = String(value.subscriptionId || "").trim();
  const customerId = String(
    value.customerId || value.stripeCustomerId || "",
  ).trim();
  return (status === "active" || status === "trialing")
    && Boolean(subscriptionId)
    && Boolean(customerId);
}

function isStandaloneLocalTrialCandidate(profile: Record<string, unknown>): boolean {
  const payments = profile.payments;
  if (!payments || typeof payments !== "object" || Array.isArray(payments)) {
    return false;
  }
  const value = payments as TrialPayments;
  return profile.role === "AccountOwner"
    && value.subscriptionStatus === "trialing"
    && value.plan !== "lifetime"
    && !String(value.subscriptionId || "").trim()
    && !String(value.customerId || value.stripeCustomerId || "").trim()
    && Number.isSafeInteger(profile.createdDate)
    && Number(profile.createdDate) >= 0;
}

/**
 * Whether a new standalone account gets the local trial. Owner decision
 * 2026-10-22: no trial for now; a new account signs in straight to the
 * paywall (the paused state: sign-in, plan picker and export). Set it back
 * to true to restore the trial for accounts created afterwards. Accounts
 * already on a trial keep it either way.
 */
export const NEW_ACCOUNT_TRIAL_ENABLED: boolean = false;

/**
 * The billing state signup writes for a new account. An account owner on the
 * trial starts "trialing" with no Stripe IDs; without the trial it starts
 * "none" and is paused until checkout. Invited members keep "trialing", the
 * same member trial they had before; a legacy invitee who waited as its own
 * account gets it when it joins (`userDetails:joinCompanyProfile`).
 */
export function initialSignupPayments(
  role: string,
  trialEnabled: boolean = NEW_ACCOUNT_TRIAL_ENABLED,
): {
  subscriptionStatus: "trialing" | "none";
  subscriptionId: "";
} {
  const trial = trialEnabled || role !== "AccountOwner";
  return { subscriptionStatus: trial ? "trialing" : "none", subscriptionId: "" };
}

/**
 * Apply the new-account trial policy at the transaction that creates a new
 * standalone account. With the trial on, stamp the forward-only trial cohort;
 * with it off, a profile that arrives "trialing" with no Stripe IDs is stored
 * as "none", so no creation path can grant a trial the policy withholds.
 * Generic profile updates must never call this: it would synthesize the
 * marker for an existing account from mutable billing or consent fields.
 */
export function applyNewStandaloneAccountTrialPolicy(
  profile: Record<string, unknown>,
  trialEnabled: boolean = NEW_ACCOUNT_TRIAL_ENABLED,
): Record<string, unknown> {
  if (!isStandaloneLocalTrialCandidate(profile)) return profile;
  if (!trialEnabled) {
    return {
      ...profile,
      payments: {
        ...(profile.payments as Record<string, unknown>),
        subscriptionStatus: "none",
      },
    };
  }
  return {
    ...profile,
    trialEnrollmentPolicyVersion: TRIAL_ENROLLMENT_COHORT_VERSION,
  };
}

export function isNewStandaloneLocalTrialProfile(profile: Record<string, unknown>): boolean {
  const payments = profile.payments;
  if (!payments || typeof payments !== "object" || Array.isArray(payments)) {
    return false;
  }
  const value = payments as TrialPayments;
  const legalAcceptance = profile.legalAcceptance;
  const acceptance = legalAcceptance
    && typeof legalAcceptance === "object"
    && !Array.isArray(legalAcceptance)
    ? legalAcceptance as Record<string, unknown>
    : null;
  return profile.trialEnrollmentPolicyVersion === TRIAL_ENROLLMENT_COHORT_VERSION
    && isStandaloneLocalTrialCandidate(profile)
    && value.plan !== "lifetime"
    && !String(value.subscriptionId || "").trim()
    && !String(value.customerId || value.stripeCustomerId || "").trim()
    && acceptance?.termsVersion === CURRENT_LEGAL_DOCUMENTS.terms.version
    && acceptance?.privacyVersion === CURRENT_LEGAL_DOCUMENTS.privacy.version
    && acceptance?.termsSha256 === CURRENT_LEGAL_DOCUMENTS.terms.sha256
    && acceptance?.privacySha256 === CURRENT_LEGAL_DOCUMENTS.privacy.sha256
    && acceptance?.source === "service_access"
    && typeof acceptance?.receiptId === "string"
    && acceptance.receiptId.length > 0
    && acceptance.receiptId.length <= 128
    && Number.isSafeInteger(acceptance.acceptedAt)
    && Number(acceptance.acceptedAt) >= Number(profile.createdDate);
}

/**
 * A new standalone trial account whose trial has not started yet. The trial
 * starts when the owner accepts the current service-access terms
 * (`ensureTrialLifecycleForNewProfile` stores `trialStartedAt`), so until
 * then the whole trial window is still ahead. This grants nothing by itself:
 * callers use it to describe the trial the account receives on acceptance.
 */
export function isAwaitingServiceAccessTrialStart(
  profile: Record<string, unknown>,
): boolean {
  return profile.trialEnrollmentPolicyVersion === TRIAL_ENROLLMENT_COHORT_VERSION
    && isStandaloneLocalTrialCandidate(profile)
    && getStoredTrialStartedAt(profile) === null
    && !isNewStandaloneLocalTrialProfile(profile);
}

export function isExpiredStandaloneTrialProfile(
  profile: Record<string, unknown>,
  now: number,
): boolean {
  const payments = profile.payments;
  const status = payments
    && typeof payments === "object"
    && !Array.isArray(payments)
    ? String((payments as Record<string, unknown>).subscriptionStatus || "")
        .trim()
        .toLowerCase()
    : "";
  const startedAt = getTrialEnrollmentStartedAt(profile);
  return status === "trialing"
    && isNewStandaloneLocalTrialProfile(profile)
    && startedAt !== null
    && now > startedAt + TRIAL_ACCESS_DURATION_MS;
}

export function getTrialEnrollmentStartedAt(profile: Record<string, unknown>) {
  if (!isNewStandaloneLocalTrialProfile(profile)) return null;
  const acceptance = profile.legalAcceptance as Record<string, unknown>;
  return Number(acceptance.acceptedAt);
}

/**
 * The trial start the lifecycle stored when the trial began. Re-accepting
 * updated legal documents must never restart a trial, so callers report this
 * stored value instead of rebuilding one from the newest acceptance.
 */
export function getStoredTrialStartedAt(profile: { trialStartedAt?: unknown } | null | undefined): number | null {
  const startedAt = profile?.trialStartedAt;
  return typeof startedAt === "number" && Number.isSafeInteger(startedAt) && startedAt >= 0 ? startedAt : null;
}

export function getTrialLifecycleDeadlines(startedAt: number) {
  return {
    startedAt,
    accessEndsAt: startedAt + TRIAL_ACCESS_DURATION_MS,
    dayFiveEmailAt: startedAt + TRIAL_FIRST_NOTICE_OFFSET_MS,
    daySevenEmailAt: startedAt + TRIAL_FINAL_NOTICE_OFFSET_MS,
    deletionAt: startedAt + TRIAL_DELETION_OFFSET_MS,
  };
}

export type TrialLifecycleScheduleRow = {
  policyVersion: string;
  state: "active" | "grace_locked" | "deletion_queued" | "deleting" | "converted";
  startedAt: number;
  accessEndsAt: number;
  dayFiveEmailAt: number;
  daySevenEmailAt: number;
  deletionAt: number;
  nextActionAt?: number;
  dayFiveEmailSentAt?: number;
  daySevenEmailSentAt?: number;
  claimedAction?: string;
  claimToken?: string;
  claimExpiresAt?: number;
  deletionClosureAttemptId?: string;
  deletionDispatchedAt?: number;
  deletionRunId?: string;
};

type TrialLifecycleCurrentSchedule = {
  policyVersion: string;
  state: "active" | "grace_locked";
  accessEndsAt: number;
  dayFiveEmailAt: number;
  daySevenEmailAt: number;
  deletionAt: number;
  nextActionAt: number | undefined;
  dayFiveEmailSentAt: number | undefined;
  daySevenEmailSentAt: number | undefined;
};

export type TrialLifecycleSchedulePatch = TrialLifecycleCurrentSchedule & {
  claimedAction: undefined;
  claimToken: undefined;
  claimExpiresAt: undefined;
};

/**
 * When an unconverted trial row next needs the scheduler: the access lock,
 * then each notice not yet sent. Undefined once the final notice went out;
 * the account then stays paused with nothing left to do.
 */
export function nextTrialLifecycleActionAt(row: {
  state: TrialLifecycleScheduleRow["state"];
  accessEndsAt: number;
  dayFiveEmailAt: number;
  daySevenEmailAt: number;
  dayFiveEmailSentAt?: number;
  daySevenEmailSentAt?: number;
}): number | undefined {
  if (row.state === "active") return row.accessEndsAt;
  if (row.daySevenEmailSentAt) return undefined;
  if (!row.dayFiveEmailSentAt) return row.dayFiveEmailAt;
  return row.daySevenEmailAt;
}

/**
 * The current schedule for a row written under an older one. Every deadline
 * only moves later (max of stored and current), so nobody loses access
 * earlier than a notice already told them. A sent notice is re-armed only
 * when the legacy deletion date it quoted moved and its new send time is
 * still ahead, so the user hears the corrected story, first notice before
 * final. Rows whose deletion was dispatched, and converted rows, keep their
 * schedule.
 */
function currentPolicySchedule(
  row: TrialLifecycleScheduleRow,
  now: number,
): TrialLifecycleCurrentSchedule | null {
  if (row.policyVersion === TRIAL_POLICY_VERSION) return null;
  if (row.state !== "active" && row.state !== "grace_locked") return null;
  if (row.deletionClosureAttemptId || row.deletionDispatchedAt || row.deletionRunId) {
    return null;
  }
  if (!Number.isSafeInteger(row.startedAt) || row.startedAt < 0) return null;
  const current = getTrialLifecycleDeadlines(row.startedAt);
  const accessEndsAt = Math.max(row.accessEndsAt, current.accessEndsAt);
  const dayFiveEmailAt = Math.max(row.dayFiveEmailAt, current.dayFiveEmailAt);
  const daySevenEmailAt = Math.max(row.daySevenEmailAt, current.daySevenEmailAt);
  const deletionAt = Math.max(row.deletionAt, current.deletionAt);
  const deletionMoved = deletionAt !== row.deletionAt;
  const dayFiveEmailSentAt = deletionMoved && dayFiveEmailAt > now
    ? undefined
    : row.dayFiveEmailSentAt;
  const daySevenEmailSentAt = deletionMoved && daySevenEmailAt > now
    ? undefined
    : row.daySevenEmailSentAt;
  const state = now < accessEndsAt ? "active" as const : "grace_locked" as const;
  const nextActionAt = nextTrialLifecycleActionAt({
    state,
    accessEndsAt,
    dayFiveEmailAt,
    daySevenEmailAt,
    dayFiveEmailSentAt,
    daySevenEmailSentAt,
  });
  return {
    policyVersion: TRIAL_POLICY_VERSION,
    state,
    accessEndsAt,
    dayFiveEmailAt,
    daySevenEmailAt,
    deletionAt,
    nextActionAt,
    dayFiveEmailSentAt,
    daySevenEmailSentAt,
  };
}

/**
 * The write that moves a row onto the current schedule, or null when it is
 * already there or must keep its schedule. A row with a live claim waits for
 * the claim's owner to finish; an expired claim is cleared so a late worker
 * from the old schedule cannot complete against the new one.
 */
export function reconcileTrialLifecycleWithCurrentPolicy(
  row: TrialLifecycleScheduleRow,
  now: number,
): TrialLifecycleSchedulePatch | null {
  if (row.claimedAction && row.claimExpiresAt !== undefined && row.claimExpiresAt > now) {
    return null;
  }
  const schedule = currentPolicySchedule(row, now);
  if (!schedule) return null;
  return {
    ...schedule,
    claimedAction: undefined,
    claimToken: undefined,
    claimExpiresAt: undefined,
  };
}

/**
 * The row as the current schedule sees it, without writing anything. Reads
 * apply it even while a notice claim is live, so access never lags the policy.
 */
export function viewTrialLifecycleUnderCurrentPolicy<T extends TrialLifecycleScheduleRow>(
  row: T,
  now: number,
): T {
  const schedule = currentPolicySchedule(row, now);
  return schedule ? { ...row, ...schedule } : row;
}

/** Whether a trial row still grants access at `now` under the current schedule. */
export function isTrialLifecycleAccessOpen(
  row: TrialLifecycleScheduleRow,
  now: number,
): boolean {
  const view = viewTrialLifecycleUnderCurrentPolicy(row, now);
  return view.state === "active" && now < view.accessEndsAt;
}

type TrialWindowChange = {
  changeType: string;
  providerThreadId: string;
  receivedAt?: number;
};

/**
 * Keeps the upserts of threads whose newest message falls inside the trial
 * window (older messages of such a thread stay, so the thread is whole).
 * `windowReached` reports that the page held a dated thread older than the
 * window: a newest-first snapshot has nothing newer left to find after it.
 * A thread with no dated upsert can't be placed in the window, so it is left
 * out and counted in `undatedThreads`; it never ends the preview.
 */
export function selectTrialEmailWindow<T extends TrialWindowChange>(
  changes: readonly T[],
  now: number,
): ReturnType<typeof selectEmailHistoryWindow<T>> {
  return selectEmailHistoryWindow(changes, now - TRIAL_EMAIL_WINDOW_MS);
}

/**
 * Keeps upserts for threads whose newest dated message is at or after
 * `windowStart`, and every delete. An undated thread is dropped and counted.
 * `windowReached` reports a thread older than the window on this page;
 * `windowPassed` reports that every dated thread on it is older, which a
 * newest-first listing cannot reach through one out-of-order old message.
 */
export function selectEmailHistoryWindow<T extends TrialWindowChange>(
  changes: readonly T[],
  windowStart: number,
): { changes: T[]; windowReached: boolean; windowPassed: boolean; undatedThreads: number } {
  const newestByThread = new Map<string, number | undefined>();
  for (const change of changes) {
    if (change.changeType !== "upsert") continue;
    const receivedAt = Number.isFinite(change.receivedAt) && Number(change.receivedAt) >= 0
      ? Math.floor(Number(change.receivedAt))
      : undefined;
    const prior = newestByThread.get(change.providerThreadId);
    newestByThread.set(
      change.providerThreadId,
      receivedAt === undefined ? prior : Math.max(prior ?? 0, receivedAt),
    );
  }
  const newest = Array.from(newestByThread.values());
  const dated = newest.filter((value): value is number => value !== undefined);
  return {
    changes: changes.filter((change) => {
      if (change.changeType !== "upsert") return true;
      const threadNewest = newestByThread.get(change.providerThreadId);
      return threadNewest !== undefined && threadNewest >= windowStart;
    }),
    windowReached: dated.some((value) => value < windowStart),
    windowPassed: dated.length > 0 && dated.every((value) => value < windowStart),
    undatedThreads: newest.filter((value) => value === undefined).length,
  };
}

export function resolveTrialEmailAllowance(args: {
  limit: number;
  discovered: number;
  reserved: number;
  requested: number;
}) {
  return Math.min(
    Math.max(0, Math.floor(args.requested)),
    Math.max(
      0,
      Math.floor(args.limit)
        - Math.floor(args.discovered)
        - Math.floor(args.reserved),
    ),
  );
}

/**
 * The lifecycle action due at `now`. There is no deletion step: after the
 * final notice an unconverted trial stays paused.
 */
export function resolveDueTrialLifecycleAction(args: {
  now: number;
  accessEndsAt: number;
  dayFiveEmailAt: number;
  daySevenEmailAt: number;
  dayFiveEmailSentAt?: number;
  daySevenEmailSentAt?: number;
}) {
  if (args.now >= args.daySevenEmailAt && !args.daySevenEmailSentAt) {
    return "day_seven_email" as const;
  }
  // The first notice is pointless once the final one went out; sending it
  // afterwards would tell the user an earlier, superseded story.
  if (
    args.now >= args.dayFiveEmailAt
    && !args.dayFiveEmailSentAt
    && !args.daySevenEmailSentAt
  ) {
    return "day_five_email" as const;
  }
  if (args.now >= args.accessEndsAt) return "lock_access" as const;
  return null;
}

export const TRIAL_ACTION_RETRY_BASE_MS = 5 * 60 * 1000;
export const TRIAL_ACTION_RETRY_MAX_MS = 6 * 60 * 60 * 1000;
// Eight consecutive failures span about 10.5 hours of backoff (5 min doubling,
// each step capped at 6 h), long enough to outlast a provider outage.
export const TRIAL_ACTION_MAX_CONSECUTIVE_FAILURES = 8;
// A claim refused for a condition that can change on its own is rechecked on
// this cadence. The refusal never counts toward the failure streak.
export const TRIAL_ACTION_REFUSAL_RECHECK_MS = TRIAL_ACTION_RETRY_MAX_MS;

export type TrialActionReleaseDecision =
  | { outcome: "retry"; nextActionAt: number; consecutiveFailureCount: number }
  | { outcome: "blocked"; consecutiveFailureCount: number };

/**
 * What a released due-action claim does next. A release without a failure
 * (the claim went stale before dispatch) retries on the base interval and
 * keeps the failure streak. A failed dispatch backs off exponentially and,
 * once the failure is terminal or the streak reaches the bound, parks the row
 * for owner review instead of retrying forever.
 */
export function decideTrialActionRelease(args: {
  now: number;
  consecutiveFailureCount?: number;
  failure?: { terminal: boolean };
}): TrialActionReleaseDecision {
  const prior = Number.isSafeInteger(args.consecutiveFailureCount)
    && Number(args.consecutiveFailureCount) > 0
    ? Number(args.consecutiveFailureCount)
    : 0;
  if (!args.failure) {
    return {
      outcome: "retry",
      nextActionAt: args.now + TRIAL_ACTION_RETRY_BASE_MS,
      consecutiveFailureCount: prior,
    };
  }
  const consecutiveFailureCount = prior + 1;
  if (
    args.failure.terminal
    || consecutiveFailureCount >= TRIAL_ACTION_MAX_CONSECUTIVE_FAILURES
  ) {
    return { outcome: "blocked", consecutiveFailureCount };
  }
  const delay = Math.min(
    TRIAL_ACTION_RETRY_MAX_MS,
    TRIAL_ACTION_RETRY_BASE_MS * 2 ** (consecutiveFailureCount - 1),
  );
  return {
    outcome: "retry",
    nextActionAt: args.now + delay,
    consecutiveFailureCount,
  };
}
